import { inject, provide, toRefs } from 'vue';

import type { EditorProductProperty } from '@getaccept/editor-lib/src/types/editor-product-property';
import type { InjectionKey, Reactive, Ref } from 'vue';

export const EDITOR_VIEW_STATE_KEY = Symbol() as InjectionKey<EditorViewState>;

export type EditorViewState = Reactive<{
  productProperties: Ref<EditorProductProperty[]>;
}>;

/**
 * Injects the editor-view state.
 *
 * NOTE: Only to be used in components that are descendants of the root component of the
 * editor-view (EditorBlockView.vue).
 *
 * @returns A reactive object of the editor-view state.
 */
export function useInjectEditorViewState() {
  const editorViewState = inject(EDITOR_VIEW_STATE_KEY);

  if (!editorViewState) {
    throw new Error('EditorViewState not provided.');
  }

  return toRefs(editorViewState);
}

/**
 * Provides the editor-view state. This is necessary to be able to inject state using
 * the useInjectEditorViewState composable.
 *
 * NOTE: Only to be used in the root component of editor-view (EditorBlockView.vue).
 *
 * @param editorViewState - The state of the editor-view to be provided.
 */
export function useProvideEditorViewState(editorViewState: EditorViewState) {
  return provide(EDITOR_VIEW_STATE_KEY, editorViewState);
}
